import Button from "@/common/elements/Button";
import FormItem from "@/common/elements/InputElements/FormItem.jsx";
import Select from "@/common/elements/InputElements/Select";
import TextField from "@/common/elements/InputElements/TextField";
import { StyledGridCol, StyledGridRow } from "@/common/styles";
import { FieldSecurityProvider } from "@/contexts/FieldSecurityContext";
import SecureFormItem from "@/common/SecureFormItem";
import {
  getOptionsFromRefList,
  generateRules,
  isNonEmptyVariable,
  convertApiFields,
  getSQLTypeToHTMLType,
} from "@/util/common_function";
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Form } from "antd";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { SCHEMA_NAME } from "@/util/common_variable";
import DynamicForm from "@/common/DynamicForm";
import { BookingsSectionTitle } from "../styles";
import api from "@/services/axios_custom";
import API_ENDPOINTS from "@/util/api";
import { fetchExtraTableRecord } from "@/util/extraColumnActions";
import { useClientParamBool } from "@/hooks/useClientParam";

export const AddressesForm = forwardRef(
  ({ formData, bookingsFieldsEnabled, startingPoint }, ref) => {
    const [form] = Form.useForm();
    const fullSearch = useSelector((state) => state.fullSearch);
    // TextField's own Globals:UppercaseDisplay handling (className-based,
    // see its own comment) doesn't reach Address1/Apt# below — their
    // Form.Item sets hasFeedback for the live validation checkmark, and
    // antd clones hasFeedback children with a fresh prop set that drops
    // whatever TextField passed down, className included. Applying the
    // uppercase rule to the Form.Item's own className instead (a plain prop
    // on a component we render directly, never cloned by antd) sidesteps
    // that entirely — see the matching ".force-uppercase-input input" rule
    // in App.css.
    const uppercaseDisplay = useClientParamBool("Globals", "UppercaseDisplay", false);

    // -----------------------------------------------------------------------
    // AddressesExtra — PCI's per-client dynamic "_U" columns for this
    // address (AddressInterfaceHelper.asp's "Additional Address Information"
    // section, shown right after the address fields). Shared by every
    // caller of AddressesList/AddressesForm (Bio's Personal Information tab,
    // WarrantAffiant's associates mode, etc.) since AddressesExtra is keyed
    // off the underlying Addresses table itself, not any particular parent.
    // -----------------------------------------------------------------------
    const [addressesExtraFields, setAddressesExtraFields] = useState([]);
    const [addressesExtraFieldsReady, setAddressesExtraFieldsReady] =
      useState(false);
    const addressesExtraTypeMapRef = useRef({});
    const addressesExtraIdRef = useRef(0);

    useEffect(() => {
      api
        .get(`${API_ENDPOINTS.getTableColumns}/AddressesExtra`)
        .then((response) => {
          const tableData = (response?.data || []).filter((d) =>
            d?.columnName?.endsWith("_U")
          );
          const converted = convertApiFields(tableData, "AddressesExtra");
          const typeMap = {};
          converted.forEach((c) => {
            typeMap[c.columnName] = getSQLTypeToHTMLType(c?.type);
          });
          addressesExtraTypeMapRef.current = typeMap;
          setAddressesExtraFields(converted);
          setAddressesExtraFieldsReady(true);
        })
        .catch((error) => {
          console.error(
            "[AddressesForm] AddressesExtra field defs fetch failed:",
            error
          );
        });
    }, []);

    // Fetches the saved AddressesExtra row when editing an existing address
    // — a brand new address has no addressId yet.
    useEffect(() => {
      if (!addressesExtraFieldsReady) return;
      const id = formData?.addressId;
      if (!isNonEmptyVariable(id)) {
        addressesExtraIdRef.current = 0;
        return;
      }

      fetchExtraTableRecord("AddressesExtra", "addressID", id)
        .then((result) => {
          if (!result) return;
          addressesExtraIdRef.current = result.raw?.AddressesExtraID ?? 0;
          const withDates = Object.keys(result.values).reduce((acc, key) => {
            const val = result.values[key];
            const htmlType = addressesExtraTypeMapRef.current[key];
            const isDateField = htmlType === "date" || htmlType === "datetime";
            acc[key] = isDateField && isNonEmptyVariable(val) ? dayjs(val) : val;
            return acc;
          }, {});
          form.setFieldsValue(withDates);
        })
        .catch((error) => {
          console.error("[AddressesForm] AddressesExtra fetch failed:", error);
        });
    }, [formData?.addressId, addressesExtraFieldsReady]);

    // -----------------------------------------------------------------------
    // Sync external formData → antd form
    // -----------------------------------------------------------------------
    useEffect(() => {
      if (formData && Object.keys(formData).length > 0) {
        form.setFieldsValue(formData);
      } else {
        form.setFieldsValue({
          addressType: "",
          addressStreetNumber: "",
          addressStreet1: "",
          addressStreet2: "",
          addressApartmentNumber: "",
          addressCity: "",
          addressState: "",
          addressZip: "",
        });
      }
    }, [formData]);

    // -----------------------------------------------------------------------
    // Expose form API to parent via ref
    // -----------------------------------------------------------------------
    useImperativeHandle(ref, () => ({
      submitForm: () => form.submit(),
      resetForm: () => form.resetFields(),
      getFormValues: () => form.getFieldsValue(),
      getFieldsError: () => form.getFieldsError(),
      isFieldsValidating: () => form.isFieldsValidating(),
      validateFields: () => form.validateFields(),
      addressesExtraFieldKeys: addressesExtraFields.map((f) => f.columnName),
      addressesExtraTypeMap: addressesExtraTypeMapRef.current,
      addressesExtraID: addressesExtraIdRef.current,
    }));

    // -----------------------------------------------------------------------
    // Render
    // -----------------------------------------------------------------------
    return (
      <FieldSecurityProvider
        entity={SCHEMA_NAME.ADDRESSES}
        startingPoint={startingPoint}
        bookingsFieldsEnabled={bookingsFieldsEnabled}
      >
        <Form
          form={form}
          initialValues={formData || {}}
          disabled={!bookingsFieldsEnabled}
        >

          {/* Row 1 — Address Type + Map button */}
          <StyledGridRow gutter={16}>
            <StyledGridCol>
              <SecureFormItem
                fieldName="addressType"
                formItemProps={{
                  name: "addressType",
                  label: "Address Type",
                  required: !fullSearch,
                  rules: !fullSearch
                    ? [{ required: true, message: "Address Type is required!" }]
                    : [],
                }}
              >
                <Select
                  options={getOptionsFromRefList("Address_Type")}
                  placeholder="Address Type"
                  showSearch={false}
                  variant="outlined"
                />
              </SecureFormItem>
            </StyledGridCol>

            {/* Map button — not a secured field, just respects bookingsFieldsEnabled */}
            <StyledGridCol>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  height: "100%",
                  paddingTop: "14px",
                }}
              >
                <Button
                  color="default"
                  type="default"
                  disabled={!bookingsFieldsEnabled}
                >
                  Map
                </Button>
              </div>
            </StyledGridCol>
          </StyledGridRow>

          {/* Row 2 — Address1 (street number) + Street */}
          <StyledGridRow gutter={16}>
            <StyledGridCol>
              <SecureFormItem
                fieldName="addressStreetNumber"
                formItemProps={{
                  name: "addressStreetNumber",
                  label: "Address1",
                  hasFeedback: true,
                  validateTrigger: "onChange",
                  className: uppercaseDisplay ? "force-uppercase-input" : undefined,
                  rules: [
                    { max: 9 },
                    ...generateRules("Addresses:AddressStreetNumber", "Address1", false),
                  ],
                }}
              >
                <TextField />
              </SecureFormItem>
            </StyledGridCol>

            <StyledGridCol flex="auto">
              <SecureFormItem
                fieldName="addressStreet1"
                formItemProps={{
                  name: "addressStreet1",
                  label: "Street",
                  rules: generateRules("Addresses:AddressStreet1", "Street", false),
                }}
              >
                <TextField />
              </SecureFormItem>
            </StyledGridCol>
          </StyledGridRow>

          {/* Row 3 — Street 2 + Apt# */}
          <StyledGridRow gutter={16}>
            <StyledGridCol flex="auto">
              <SecureFormItem
                fieldName="addressStreet2"
                formItemProps={{ name: "addressStreet2", label: "Street 2" }}
              >
                <TextField />
              </SecureFormItem>
            </StyledGridCol>

            <StyledGridCol>
              <SecureFormItem
                fieldName="addressApartmentNumber"
                formItemProps={{
                  name: "addressApartmentNumber",
                  label: "Apt#",
                  hasFeedback: true,
                  validateTrigger: "onChange",
                  className: uppercaseDisplay ? "force-uppercase-input" : undefined,
                  rules: [{ max: 8 }],
                }}
              >
                <TextField />
              </SecureFormItem>
            </StyledGridCol>
          </StyledGridRow>

          {/* Row 4 — City + State + Zip */}
          <StyledGridRow gutter={16}>
            <StyledGridCol span={8}>
              <SecureFormItem
                fieldName="addressCity"
                formItemProps={{
                  name: "addressCity",
                  label: "City",
                  rules: generateRules("Addresses:AddressCity", "City", false),
                }}
              >
                <TextField />
              </SecureFormItem>
            </StyledGridCol>

            <StyledGridCol span={8}>
              <SecureFormItem
                fieldName="addressState"
                formItemProps={{ name: "addressState", label: "ST" }}
              >
                <Select
                  options={getOptionsFromRefList("USState")}
                  placeholder="ST"
                  showSearch={false}
                  variant="outlined"
                />
              </SecureFormItem>
            </StyledGridCol>

            <StyledGridCol span={8}>
              <SecureFormItem
                fieldName="addressZip"
                formItemProps={{
                  name: "addressZip",
                  label: "Zip Code",
                  rules: [
                    {
                      validator: (_, value) => {
                        if (!value) return Promise.resolve();
                        if (/^[0-9-]{1,10}$/.test(value)) return Promise.resolve();
                        return Promise.reject(
                          "The zip code field allows numbers and dashes only and must be 10 characters or less"
                        );
                      },
                    },
                    ...generateRules("Addresses:AddressZip", "Zip Code", false),
                  ],
                }}
              >
                <TextField />
              </SecureFormItem>
            </StyledGridCol>
          </StyledGridRow>

          {addressesExtraFields.length > 0 && (
            <>
              <BookingsSectionTitle>
                <span>Additional Address Information</span>
              </BookingsSectionTitle>
              <StyledGridRow gutter={16}>
                {addressesExtraFields.map((sf, index) => (
                  <DynamicForm
                    key={index}
                    formAttributes={sf}
                    form={form}
                    bookingsFieldsEnabled={bookingsFieldsEnabled}
                  />
                ))}
              </StyledGridRow>
            </>
          )}

        </Form>
      </FieldSecurityProvider>
    );
  }
);

export default AddressesForm;